<template>
    <div class="h-full w-full flex flex-col p-8 text-left">
        <h1 class="text-2xl font-bold underline">Applicant Details</h1>
        <div class="flex flex-row-reverse flex-wrap mt-4">
            <div class="w-full sm:w-1/2 flex flex-col">
                <img class="w-56 p-4 border" :src="getProfilePicture" alt="Profile Picture">
            </div>
            <div class="w-full sm:w-1/2 flex flex-col mt-4 sm:mt-0">
                <div class="flex flex-wrap items-center">
                    <div class="font-bold w-40">Name</div>
                    <div>: {{ jobApplication.user.name }}</div>
                </div>
                <div class="flex flex-wrap items-center mt-2">
                    <div class="font-bold w-40">Email</div>
                    <div>: {{ jobApplication.user.email }}</div>
                </div>
                <div class="flex flex-wrap items-center mt-2">
                    <div class="font-bold w-40">Applying For</div>
                    <div>: {{ jobApplication.job.job_title }} - {{ jobApplication.job.position_name }}</div>
                </div>
                <div class="flex flex-wrap items-center mt-2">
                    <div class="font-bold w-40">Application Date</div>
                    <div>: {{ getApplicationDate }}</div>
                </div>
                <div class="flex flex-wrap items-center mt-2">
                    <div class="font-bold w-40">Application Status</div>
                    <div>: {{ getApplicationStatus }}</div>
                </div>
                <div v-if="showEmployer" class="flex flex-wrap items-center mt-2">
                    <div class="font-bold w-40">Employer</div>
                    <div>: {{ jobApplication.job.partner ? jobApplication.job.partner.en_company_name :
                        jobApplication.job.employer_name }}</div>
                </div>
            </div>
        </div>
        <h1 class="text-2xl font-bold underline mt-8">Recruitment Journey</h1>
        <div v-if="role == 'partner' || ((role == 'admin' || role == 'superadmin') && isEditableForMe)"
            class="flex flex-col mt-6 pb-6">
            <div class="flex flex-col border" :class="getBorderColor('screened')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('screened')">
                    Screened In
                </div>
                <div v-if="showSubmitButton('screened')" class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been screened for the position?</div>
                        <div class="flex items-center">
                            <div class="flex items-center mr-4">
                                <input class="mr-2" type="radio" id="screenedYes" v-model="screened.val"
                                    @blur="clearValidity('screened')" :value="true">
                                <label for="screenedYes">Yes</label>
                            </div>
                            <div class="flex items-center">
                                <input class="mr-2" type="radio" id="screenedNo" v-model="screened.val"
                                    @blur="clearValidity('screened')" :value="false">
                                <label for="screenedNo">No</label>
                            </div>
                        </div>
                    </div>
                    <transition name="fade" mode="out-in">
                        <p v-if="!screened.isValid" class="h-4 text-xs text-red-600">
                            {{ screened.msg }}
                        </p>
                    </transition>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Add your remarks</div>
                        <div class="flex flex-col flex-grow">
                            <div class="h-20 border p-4 flex-grow">
                                <textarea v-model="screenedRemarks.val" @blur="clearValidity('screenedRemarks')"
                                    class="resize-none w-full h-full outline-none text-sm"
                                    placeholder="Write here..."></textarea>
                            </div>
                            <transition name="fade" mode="out-in">
                                <p v-if="!screenedRemarks.isValid" class="h-4 text-xs text-red-600">
                                    {{ screenedRemarks.msg }}
                                </p>
                            </transition>
                        </div>

                    </div>
                    <div class="flex justify-end mt-4">
                        <div @click.stop="updateJobApplicationStatus('screened')"
                            class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                            Submit</div>
                    </div>
                </div>
                <div v-else class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been screened for the position?</div>
                        <div class="flex items-center">{{ screened.val === null ? "Not updated yet" : (screened.val ===
                            true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ screenedRemarks.val }}</div>
                    </div>
                    <div v-if="screened_time" class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(screened_time) }}</div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col border mt-6" :class="getBorderColor('interviewed')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('interviewed')">
                    Interviewed
                </div>
                <div v-if="showSubmitButton('interviewed')" class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been interviewed for the position?</div>
                        <div class="flex items-center">
                            <div class="flex items-center mr-4">
                                <input class="mr-2" type="radio" id="interviewedYes" v-model="interviewed.val"
                                    @blur="clearValidity('interviewed')" :value="true">
                                <label for="interviewedYes">Yes</label>
                            </div>
                            <div class="flex items-center">
                                <input class="mr-2" type="radio" id="interviewedNo" v-model="interviewed.val"
                                    @blur="clearValidity('interviewed')" :value="false">
                                <label for="interviewedNo">No</label>
                            </div>
                        </div>
                    </div>
                    <transition name="fade" mode="out-in">
                        <p v-if="!interviewed.isValid" class="h-4 text-xs text-red-600">
                            {{ interviewed.msg }}
                        </p>
                    </transition>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Add your remarks</div>
                        <div class="flex flex-col flex-grow">
                            <div class="h-20 border p-4 flex-grow">
                                <textarea v-model="interviewedRemarks.val" @blur="clearValidity('interviewedRemarks')"
                                    class="resize-none w-full h-full outline-none text-sm"
                                    placeholder="Write here..."></textarea>
                            </div>
                            <transition name="fade" mode="out-in">
                                <p v-if="!interviewedRemarks.isValid" class="h-4 text-xs text-red-600">
                                    {{ interviewedRemarks.msg }}
                                </p>
                            </transition>
                        </div>

                    </div>
                    <div class="flex justify-end mt-4">
                        <div @click.stop="updateJobApplicationStatus('interviewed')"
                            class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                            Submit</div>
                    </div>
                </div>
                <div v-else class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been interviewed for the position?</div>
                        <div class="flex items-center">{{ interviewed.val === null ? "Not updated yet" :
                            (interviewed.val === true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ interviewedRemarks.val }}</div>
                    </div>
                    <div v-if="interviewed_time" class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(interviewed_time) }}</div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col border mt-6" :class="getBorderColor('recruitment_process')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('recruitment_process')">
                    In Recruitment Process
                </div>
                <div v-if="showSubmitButton('recruitment_process')" class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Is the applicant currently in the recruitment process?</div>
                        <div class="flex items-center">
                            <div class="flex items-center mr-4">
                                <input class="mr-2" type="radio" id="recruitment_processYes"
                                    v-model="recruitment_process.val" @blur="clearValidity('recruitment_process')"
                                    :value="true">
                                <label for="recruitment_processYes">Yes</label>
                            </div>
                            <div class="flex items-center">
                                <input class="mr-2" type="radio" id="recruitment_processNo"
                                    v-model="recruitment_process.val" @blur="clearValidity('recruitment_process')"
                                    :value="false">
                                <label for="recruitment_processNo">No</label>
                            </div>
                        </div>
                    </div>
                    <transition name="fade" mode="out-in">
                        <p v-if="!recruitment_process.isValid" class="h-4 text-xs text-red-600">
                            {{ recruitment_process.msg }}
                        </p>
                    </transition>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Add your remarks</div>
                        <div class="flex flex-col flex-grow">
                            <div class="h-20 border p-4 flex-grow">
                                <textarea v-model="recruitment_processRemarks.val"
                                    @blur="clearValidity('recruitment_processRemarks')"
                                    class="resize-none w-full h-full outline-none text-sm"
                                    placeholder="Write here..."></textarea>
                            </div>
                            <transition name="fade" mode="out-in">
                                <p v-if="!recruitment_processRemarks.isValid" class="h-4 text-xs text-red-600">
                                    {{ recruitment_processRemarks.msg }}
                                </p>
                            </transition>
                        </div>
                    </div>
                    <div class="flex justify-end mt-4">
                        <div @click.stop="updateJobApplicationStatus('recruitment_process')"
                            class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                            Submit</div>
                    </div>
                </div>
                <div v-else class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Is the applicant currently in the recruitment process?</div>
                        <div class="flex items-center">{{ recruitment_process.val === null ? "Not updated yet" :
                            (recruitment_process.val === true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ recruitment_processRemarks.val }}</div>
                    </div>
                    <div v-if="recruitment_process_time" class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(recruitment_process_time)
                            }}</div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col border mt-6" :class="getBorderColor('hired')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('hired')">
                    Hired
                </div>
                <div v-if="showSubmitButton('hired')" class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been hired for the position?</div>
                        <div class="flex items-center">
                            <div class="flex items-center mr-4">
                                <input class="mr-2" type="radio" id="hiredYes" v-model="hired.val"
                                    @blur="clearValidity('hired')" :value="true">
                                <label for="hiredYes">Yes</label>
                            </div>
                            <div class="flex items-center">
                                <input class="mr-2" type="radio" id="hiredNo" v-model="hired.val"
                                    @blur="clearValidity('hired')" :value="false">
                                <label for="hiredNo">No</label>
                            </div>
                        </div>
                    </div>
                    <transition name="fade" mode="out-in">
                        <p v-if="!hired.isValid" class="h-4 text-xs text-red-600">
                            {{ hired.msg }}
                        </p>
                    </transition>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Add your remarks</div>
                        <div class="flex flex-col flex-grow">
                            <div class="h-20 border p-4 flex-grow">
                                <textarea v-model="hiredRemarks.val" @blur="clearValidity('hiredRemarks')"
                                    class="resize-none w-full h-full outline-none text-sm"
                                    placeholder="Write here..."></textarea>
                            </div>
                            <transition name="fade" mode="out-in">
                                <p v-if="!hiredRemarks.isValid" class="h-4 text-xs text-red-600">
                                    {{ hiredRemarks.msg }}
                                </p>
                            </transition>
                        </div>

                    </div>
                    <div class="flex justify-end mt-4">
                        <div @click.stop="updateJobApplicationStatus('hired')"
                            class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                            Submit</div>
                    </div>
                </div>
                <div v-else class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been hired for the position?</div>
                        <div class="flex items-center">{{ hired.val === null ? "Not updated yet" : (hired.val === true ?
                            "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ hiredRemarks.val }}</div>
                    </div>
                    <div v-if="hired_time" class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(hired_time) }}</div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="flex flex-col mt-6 pb-6">
            <div class="flex flex-col border" :class="getBorderColor('screened')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('screened')">
                    Screened In
                </div>
                <div class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been screened for the position?</div>
                        <div class="flex items-center">{{ screened.val === null ? "Not updated yet" : (screened.val ===
                            true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ screenedRemarks.val }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(screened_time) }}</div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col border mt-6" :class="getBorderColor('interviewed')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('interviewed')">
                    Interviewed
                </div>
                <div class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been interviewed for the position?</div>
                        <div class="flex items-center">{{ interviewed.val === null ? "Not updated yet" :
                            (interviewed.val === true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ interviewedRemarks.val }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(interviewed_time) }}</div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col border mt-6" :class="getBorderColor('recruitment_process')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('recruitment_process')">
                    In Recruitment Process
                </div>
                <div class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Is the applicant currently in the recruitment process?</div>
                        <div class="flex items-center">{{ recruitment_process.val === null ? "Not updated yet" :
                            (recruitment_process.val === true ? "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ recruitment_processRemarks.val }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(recruitment_process_time)
                            }}</div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col border mt-6" :class="getBorderColor('hired')">
                <div class="border-b p-4 text-lg font-semibold uppercase text-white"
                    :class="getBorderColorAndBackground('hired')">
                    Hired
                </div>
                <div class="p-4 flex flex-col">
                    <div class="flex items-center">
                        <div class="font-semibold mr-4">Has the applicant been hired for the position?</div>
                        <div class="flex items-center">{{ hired.val === null ? "Not updated yet" : (hired.val === true ?
                            "YES" : "NO") }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Remarks: </div>
                        <div class="flex flex-col flex-grow">{{ hiredRemarks.val }}</div>
                    </div>
                    <div class="flex mt-2">
                        <div class="font-semibold mr-4">Updated At: </div>
                        <div class="flex flex-col flex-grow">{{ getFormattedDateWithTimeAmPm(hired_time) }}</div>
                    </div>
                </div>
            </div>
        </div>
        <base-page-spinner v-if="isLoading" type="spin"></base-page-spinner>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import { formattedDateWithTimeAmPm } from "../../../../helpers/utils";
export default {
    name: "recruitment-journey-workflow",
    emits: ["update"],
    props: ["jobApplication"],
    data() {
        return {
            screened: {
                val: null,
                isValid: true,
                msg: "",
            },
            screenedRemarks: {
                val: null,
                isValid: true,
                msg: "",
            },
            interviewed: {
                val: null,
                isValid: true,
                msg: "",
            },
            interviewedRemarks: {
                val: null,
                isValid: true,
                msg: "",
            },
            recruitment_process: {
                val: null,
                isValid: true,
                msg: "",
            },
            recruitment_processRemarks: {
                val: null,
                isValid: true,
                msg: "",
            },
            hired: {
                val: null,
                isValid: true,
                msg: "",
            },
            hiredRemarks: {
                val: null,
                isValid: true,
                msg: "",
            },
            formIsValid: true,
            isLoading: false,
            screened_time: null,
            interviewed_time: null,
            recruitment_process_time: null,
            hired_time: null,
        };
    },
    created() {
        this.setData();
    },
    computed: {
        userId() {
            return this.$store.getters.userId;
        },
        role() {
            return this.$store.getters.role;
        },
        showEmployer() {
            if (this.role == 'superadmin' || this.role == 'admin') {
                return true;
            } else {
                return false;
            }
        },
        getApplicationStatus() {
            if (this.jobApplication.is_approved === null) {
                return "Pending";
            } else if (this.jobApplication.is_approved === false) {
                return "Rejected";
            } else {
                if (this.jobApplication.job_application_status.hired === null) {
                    if (this.jobApplication.job_application_status.recruitment_process === null) {
                        if (this.jobApplication.job_application_status.interviewed === null) {
                            if (this.jobApplication.job_application_status.screened === null) {
                                return "Proceeded to recruitment journey";
                            } else {
                                if (this.jobApplication.job_application_status.screened === true) {
                                    return "Screened In";
                                } else {
                                    return "Not Screened In";
                                }
                            }
                        } else {
                            if (this.jobApplication.job_application_status.interviewed === true) {
                                return "Interviewed";
                            } else {
                                return "Not Interviewed";
                            }
                        }
                    } else {
                        if (this.jobApplication.job_application_status.recruitment_process === true) {
                            return "In Recruitment Process";
                        } else {
                            return "Not In Recruitment Process";
                        }
                    }
                } else {
                    if (this.jobApplication.job_application_status.hired === true) {
                        return "Hired";
                    } else {
                        return "Not hired";
                    }
                }
            }
        },
        getProfilePicture() {
            if (this.jobApplication.user.profile_picture_url == '/storage/alumni-profile-pictures/default-avatar.jpg') {
                return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${this.jobApplication.user.students.student_id}/${this.jobApplication.user.students.entity}`;
            } else {
                return process.env.VUE_APP_PUBLIC_URL + this.jobApplication.user.profile_picture_url;
            }
        },
        getApplicationDate() {
            return formattedDateWithTimeAmPm(this.jobApplication.created_at);
        },
        isEditableForMe() {
            if (this.jobApplication.job.created_by_user_id == this.userId) {
                return true;
            } else {
                return false;
            }
        },
    },
    methods: {
        getFormattedDateWithTimeAmPm(date) {
            if (date) {
                return formattedDateWithTimeAmPm(date);
            } else {
                return "";
            }
        },
        setData() {
            this.screened.val = this.jobApplication.job_application_status.screened;
            this.screenedRemarks.val = this.jobApplication.job_application_status.screened_remarks;
            this.screened_time = this.jobApplication.job_application_status.screened_time;
            this.interviewed.val = this.jobApplication.job_application_status.interviewed;
            this.interviewedRemarks.val = this.jobApplication.job_application_status.interviewed_remarks;
            this.interviewed_time = this.jobApplication.job_application_status.interviewed_time;
            this.recruitment_process.val = this.jobApplication.job_application_status.recruitment_process;
            this.recruitment_processRemarks.val = this.jobApplication.job_application_status.recruitment_process_remarks;
            this.recruitment_process_time = this.jobApplication.job_application_status.recruitment_process_time;
            this.hired.val = this.jobApplication.job_application_status.hired;
            this.hiredRemarks.val = this.jobApplication.job_application_status.hired_remarks;
            this.hired_time = this.jobApplication.job_application_status.hired_time;
        },
        getBorderColor(stage) {
            if (this.jobApplication.job_application_status[stage] === null) {
                return "border-ternary";
            } else if (this.jobApplication.job_application_status[stage] === false) {
                return "border-red-600";
            } else {
                return "border-green-600";
            }
        },
        getBorderColorAndBackground(stage) {
            if (this.jobApplication.job_application_status[stage] === null) {
                return "border-ternary bg-ternary";
            } else if (this.jobApplication.job_application_status[stage] === false) {
                return "border-red-600 bg-red-600";
            } else {
                return "border-green-600 bg-green-600";
            }
        },
        showSubmitButton(stage) {
            if (this.jobApplication.job_application_status[stage] === null) {
                if (stage == 'interviewed') {
                    if (this.jobApplication.job_application_status.screened === true) {
                        return true;
                    } else {
                        return false;
                    }
                } else if (stage == 'recruitment_process') {
                    if (this.jobApplication.job_application_status.interviewed === true) {
                        return true;
                    } else {
                        return false;
                    }
                } else if (stage == 'hired') {
                    if (this.jobApplication.job_application_status.recruitment_process === true) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            } else if (this.jobApplication.job_application_status[stage] === false) {
                return false;
            } else {
                return false;
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm(stage) {
            this.formIsValid = true;

            if (this[stage].val === null) {
                this[stage].isValid = false;
                this[stage].msg = "You must select Yes/No.";
                this.formIsValid = false;
            } else if (this[stage].val === false) {
                if (!this[stage + 'Remarks'].val) {
                    this[stage + 'Remarks'].isValid = false;
                    this[stage + 'Remarks'].msg = "You must add the remarks!";
                    this.formIsValid = false;
                }
            }

        },
        async updateJobApplicationStatus(stage) {
            const status = this[stage].val;
            const remarks = this[stage + 'Remarks'].val;

            this.validateForm(stage);

            if (!this.formIsValid) {
                return;
            }

            const UPDATE_MUTATION = gql`
                mutation UpdateJobApplicationStatus($token: String!, $stage: String!, $status: Boolean!, $remarks: String) {
                    updateJobApplicationStatus(token: $token, stage: $stage, status: $status, remarks: $remarks) {
                        error
                        message
                    }
                }
            `;

            let error_msg = null;
            let success_msg = null;
            this.isLoading = true;

            try {
                await this.$apollo.mutate({
                    mutation: UPDATE_MUTATION,
                    variables: {
                        token: this.jobApplication.job_application_status.token,
                        stage: stage,
                        status: status,
                        remarks: remarks,
                    }
                }).then((data) => {
                    if (data.data.updateJobApplicationStatus.error) {
                        error_msg = data.data.updateJobApplicationStatus.message;
                    } else {
                        success_msg = data.data.updateJobApplicationStatus.message;
                    }
                }).catch((error) => {
                    console.error(error)
                })
            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }


            this.$emit('update');

            const Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 1000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', this.$swal.stopTimer)
                    toast.addEventListener('mouseleave', this.$swal.resumeTimer)
                }
            })

            if (error_msg == null) {
                Toast.fire({
                    icon: 'success',
                    title: success_msg
                })
            } else {
                Toast.fire({
                    icon: 'error',
                    title: error_msg
                })
            }

            this.isLoading = false;

        },
    },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0px;
}
</style>